/**
 * Admin Launch Review Page
 *
 * Displays detailed launch information for admin review.
 * Allows approving or rejecting launches that are pending approval.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getAdminLaunchDetail,
  approveLaunch,
  rejectLaunch,
  resumeAutoPausedLaunch,
  setAutoSettle,
  setLaunchServiceFee,
  setPublicPhasePriceLocked,
  getAdminLaunchRefunds,
  processAdminRefund,
  getAdminLaunchItems,
  getAdminLaunchMints,
  getAdminLaunchSettlements,
  updateLaunchSocialLinks,
  resolveAdminMint,
  bulkResolveAdminMints,
  setLaunchReferralConfig,
  getLaunchClaimTokens,
  generateLaunchClaimTokens,
  voidLaunchClaimToken,
  setLaunchClaimOnly,
  exportClaimTokensCsv,
  resetLaunchClaimToken,
  downloadClaimLinksCsv,
  type AdminClaimTokenList,
  type AdminLaunchDetail,
  type AdminLaunchItem,
  type AdminRefundMint,
  type AdminLaunchItemDetail,
  type AdminLaunchMint,
  type AdminLaunchSettlement,
  type AdminTraitCategory,
  type ResolveMintRequest,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { LaunchStatusBadge } from '../components/admin/LaunchStatusBadge.js';
import { useNotificationStore } from '../store/notificationStore.js';
import { is3DModelUrl } from '../utils/media';
import { mintRewardsApi } from '../api/mint-rewards.js';
import type { ContestView } from '../api/sweepstakes.js';

// Placeholder image for items without images
const NO_IMAGE_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y0ZjRmNCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM5OTkiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';

function loadPreviewImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load: ${src}`));
    img.src = src;
  });
}

/**
 * Generate random trait picks for one preview
 */
function randomPicks(categories: AdminTraitCategory[]): Record<string, number> {
  const picks: Record<string, number> = {};
  for (const cat of categories) {
    if (cat.values.length > 0) {
      picks[cat.id] = Math.floor(Math.random() * cat.values.length);
    }
  }
  return picks;
}

/**
 * Draw a composition onto a canvas
 */
async function drawComposition(
  canvas: HTMLCanvasElement,
  baseImageUrl: string,
  categories: AdminTraitCategory[],
  picks: Record<string, number>,
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const SIZE = 512;
  canvas.width = SIZE;
  canvas.height = SIZE;
  ctx.clearRect(0, 0, SIZE, SIZE);

  const sortedCats = [...categories].sort((a, b) => a.z_index - b.z_index);

  // Background layers (z_index 0)
  for (const cat of sortedCats) {
    if (cat.z_index > 0) break;
    const idx = picks[cat.id];
    const value = idx !== undefined ? cat.values[idx] : undefined;
    if (value?.image_url) {
      try {
        const img = await loadPreviewImage(value.image_url);
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
      } catch { /* skip */ }
    }
  }

  // Base image
  try {
    const baseImg = await loadPreviewImage(baseImageUrl);
    ctx.drawImage(baseImg, 0, 0, SIZE, SIZE);
  } catch { /* skip */ }

  // Trait layers (z_index > 0)
  for (const cat of sortedCats) {
    if (cat.z_index <= 0) continue;
    const idx = picks[cat.id];
    const value = idx !== undefined ? cat.values[idx] : undefined;
    if (value?.image_url) {
      try {
        const img = await loadPreviewImage(value.image_url);
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
      } catch { /* skip */ }
    }
  }
}

const PREVIEW_COUNT = 10;

/**
 * Grid of 10 random compose composition previews
 */
function ComposePreviewGrid({
  baseImageUrl,
  categories,
}: {
  baseImageUrl: string;
  categories: AdminTraitCategory[];
}) {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const [allPicks, setAllPicks] = useState<Record<string, number>[]>([]);
  const [composing, setComposing] = useState(false);

  const shuffle = useCallback(() => {
    setAllPicks(Array.from({ length: PREVIEW_COUNT }, () => randomPicks(categories)));
  }, [categories]);

  // Auto-generate on mount
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && categories.length > 0) {
      initializedRef.current = true;
      shuffle();
    }
  }, [categories, shuffle]);

  // Draw all canvases when picks change
  useEffect(() => {
    if (allPicks.length === 0) return;
    let cancelled = false;
    setComposing(true);

    const drawAll = async () => {
      await Promise.all(
        allPicks.map(async (picks, i) => {
          const canvas = canvasRefs.current[i];
          if (canvas && !cancelled) {
            await drawComposition(canvas, baseImageUrl, categories, picks);
          }
        }),
      );
      if (!cancelled) setComposing(false);
    };

    drawAll();
    return () => { cancelled = true; };
  }, [allPicks, baseImageUrl, categories]);

  // Build trait labels for a given pick set
  const getTraitLabel = (picks: Record<string, number>) =>
    categories
      .sort((a, b) => a.z_index - b.z_index)
      .map((cat) => {
        const idx = picks[cat.id];
        const v = idx !== undefined ? cat.values[idx] : undefined;
        return v ? `${cat.name}: ${v.name}` : null;
      })
      .filter(Boolean);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Random builds from base image + one trait per category.
          {composing && <span className="ml-2 text-blue-500">Composing...</span>}
        </p>
        <button
          onClick={shuffle}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          Shuffle All
        </button>
      </div>
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {allPicks.map((picks, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-2">
            <canvas
              ref={(el) => { canvasRefs.current[i] = el; }}
              width={512}
              height={512}
              className="w-full aspect-square rounded-md bg-gray-100"
              style={{ imageRendering: 'auto' }}
            />
            <div className="mt-1.5 space-y-0.5">
              {getTraitLabel(picks).map((label, j) => (
                <div key={j} className="text-[10px] text-gray-500 truncate" title={label!}>
                  {label}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Format XRP from drops
 */
function formatXrp(drops: number | null | undefined): string {
  if (drops === null || drops === undefined) return '-';
  return `${(Number(drops) / 1_000_000).toFixed(2)} XRP`;
}

/**
 * Format percentage from XRPL transfer fee (0-50000 where 50000 = 50%)
 */
function formatRoyalty(transferFee: number): string {
  return `${(transferFee / 1000).toFixed(2)}%`;
}

/**
 * Info card component
 */
function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">{title}</h3>
      {children}
    </div>
  );
}

/**
 * Info row component
 */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

/**
 * Editable info row - shows value with pencil icon, inline text input on click
 */
function EditableInfoRow({
  label,
  value,
  onSave,
  placeholder,
  renderDisplay,
}: {
  label: string;
  value: string | null;
  onSave: (newValue: string | null) => Promise<void>;
  placeholder?: string;
  renderDisplay?: (val: string) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = inputValue.trim();
    const newVal = trimmed === '' ? null : trimmed;
    if (newVal === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(newVal);
      setEditing(false);
    } catch {
      // error handled by caller
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setInputValue(value || '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-100">
        <span className="text-sm text-gray-500 shrink-0">{label}</span>
        <div className="flex items-center gap-1.5">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            placeholder={placeholder}
            disabled={saving}
            className="rounded border border-gray-300 px-2 py-1 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {saving && <span className="text-xs text-gray-400">Saving...</span>}
        </div>
      </div>
    );
  }

  const displayValue = value
    ? (renderDisplay ? renderDisplay(value) : <span className="text-sm font-medium text-gray-900">{value}</span>)
    : <span className="text-sm font-medium text-gray-900">-</span>;

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 group">
      <span className="text-sm text-gray-500">{label}</span>
      <div className="flex items-center gap-1.5">
        {displayValue}
        <button
          onClick={() => { setInputValue(value || ''); setEditing(true); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
          title={`Edit ${label.toLowerCase()}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Modal to display launch item metadata
 */
function ItemMetadataModal({
  item,
  onClose,
}: {
  item: AdminLaunchItem;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {item.name || `#${item.token_number}`}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {item.original_image_key && (
            is3DModelUrl(item.original_image_key) ? (
              item.thumbnail_key ? (
                <img
                  src={item.thumbnail_key}
                  alt={item.name || `#${item.token_number}`}
                  className="w-full rounded-lg"
                />
              ) : (
                <div className="aspect-square w-full rounded-lg bg-gray-100 flex flex-col items-center justify-center gap-2">
                  <span style={{ fontSize: '48px' }}>🧊</span>
                  <span className="text-sm text-gray-500">3D Model (.glb)</span>
                </div>
              )
            ) : (
              <img
                src={item.original_image_key}
                alt={item.name || `#${item.token_number}`}
                className="w-full rounded-lg"
              />
            )
          )}
          <div className="space-y-2">
            <InfoRow label="Token #" value={String(item.token_number)} />
            {item.name && <InfoRow label="Name" value={item.name} />}
            {item.description && (
              <div className="py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Description</span>
                <p className="mt-1 text-sm text-gray-900">{item.description}</p>
              </div>
            )}
            <InfoRow label="Status" value={<StatusBadge status={item.status} />} />
            {item.image_cid && <InfoRow label="Image CID" value={item.image_cid} />}
            {item.metadata_uri && <InfoRow label="Metadata URI" value={item.metadata_uri} />}
          </div>
          {(() => {
            const attrs = Array.isArray(item.attributes) ? item.attributes as any[] : [];
            if (attrs.length === 0) return null;
            return (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Attributes</h4>
                <div className="grid grid-cols-2 gap-2">
                  {attrs.map((attr: any, i: number) => (
                    <div key={i} className="rounded bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">{attr.trait_type}</div>
                      <div className="text-sm font-medium text-gray-900">{String(attr.value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/**
 * Generic status badge with color coding
 */
const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  reserved: 'bg-blue-100 text-blue-800',
  minting: 'bg-indigo-100 text-indigo-800',
  minted: 'bg-cyan-100 text-cyan-800',
  transferring: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-orange-100 text-orange-800',
  payment_pending: 'bg-yellow-100 text-yellow-800',
  payment_confirmed: 'bg-teal-100 text-teal-800',
  approved: 'bg-green-100 text-green-800',
  processing: 'bg-indigo-100 text-indigo-800',
};

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/**
 * Truncated hash/address with copy and optional link
 */
function TruncatedId({ value, href }: { value: string; href?: string }) {
  const [copied, setCopied] = useState(false);
  const truncated = value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const content = (
    <span
      className="font-mono text-xs cursor-pointer hover:text-blue-700"
      onClick={handleCopy}
      title={copied ? 'Copied!' : `Click to copy: ${value}`}
    >
      {copied ? 'Copied!' : truncated}
    </span>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
        {content}
      </a>
    );
  }
  return content;
}

/**
 * Pagination controls component
 */
function PaginationControls({
  pagination,
  onPageChange,
  label = 'items',
}: {
  pagination: { page: number; limit: number; total: number; totalPages: number };
  onPageChange: (page: number) => void;
  label?: string;
}) {
  if (pagination.totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
      <div className="text-sm text-gray-700">
        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
        {pagination.total} {label}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={pagination.page <= 1}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          First
        </button>
        <button
          onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          disabled={pagination.page <= 1}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-sm text-gray-700">
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(pagination.totalPages, pagination.page + 1))}
          disabled={pagination.page >= pagination.totalPages}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
        <button
          onClick={() => onPageChange(pagination.totalPages)}
          disabled={pagination.page >= pagination.totalPages}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Last
        </button>
      </div>
    </div>
  );
}

/**
 * Status filter tabs with counts
 */
function StatusFilterTabs({
  statusCounts,
  activeStatus,
  onStatusChange,
}: {
  statusCounts: Record<string, number>;
  activeStatus: string;
  onStatusChange: (status: string) => void;
}) {
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const tabs = [
    { key: 'all', label: 'All', count: total },
    ...Object.entries(statusCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: key.replace(/_/g, ' '), count })),
  ];

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {tabs.map((tab) => {
        const isActive = activeStatus === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onStatusChange(tab.key)}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors capitalize ${
              isActive
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-blue-200' : 'bg-gray-200'}`}>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Rejection modal component
 */
function RejectModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  // Reset reason when modal closes
  const handleClose = () => {
    setReason('');
    onClose();
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason(''); // Reset after submit
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Reject Launch</h3>
        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rejection Reason
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Provide a reason for rejection that will be shown to the creator..."
            rows={4}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            required
            autoFocus
          />
          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !reason.trim()}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Rejecting...' : 'Reject Launch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AdminLaunchReviewPage() {
  const { id } = useParams<{ id: string }>();
  const notifications = useNotificationStore();
  const [launch, setLaunch] = useState<AdminLaunchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Resume state
  const [resumeLoading, setResumeLoading] = useState(false);

  // Auto-settle toggle state
  const [autoSettleLoading, setAutoSettleLoading] = useState(false);
  // Service fee editor state
  const [editingServiceFee, setEditingServiceFee] = useState(false);
  const [serviceFeeInput, setServiceFeeInput] = useState('');
  const [serviceFeeLoading, setServiceFeeLoading] = useState(false);
  // Public phase price locked toggle state
  const [priceLockLoading, setPriceLockLoading] = useState(false);

  // Claim tokens (one redeemable link per copy) state
  const [claimTokens, setClaimTokens] = useState<AdminClaimTokenList | null>(null);
  const [claimTokensLoading, setClaimTokensLoading] = useState(false);
  const [claimOnlyLoading, setClaimOnlyLoading] = useState(false);
  // One-time plaintext links from the most recent generate (never re-fetchable)
  const [generatedLinks, setGeneratedLinks] = useState<Array<{ serial: number | null; claim_url: string }> | null>(null);

  // Refund management state
  const [refunds, setRefunds] = useState<AdminRefundMint[]>([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [showRefundSection, setShowRefundSection] = useState(false);
  const [processingRefundId, setProcessingRefundId] = useState<string | null>(null);
  const [refundNotes, setRefundNotes] = useState('');
  const [refundTxHash, setRefundTxHash] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<AdminRefundMint | null>(null);

  // Mint contest state
  const [mintContest, setMintContest] = useState<ContestView | null>(null);
  const [mintContestLoading, setMintContestLoading] = useState(false);

  // Item metadata modal
  const [selectedSampleItem, setSelectedSampleItem] = useState<AdminLaunchItem | null>(null);

  // Tab section state
  type DetailTab = 'items' | 'mints' | 'settlements';
  const [activeTab, setActiveTab] = useState<DetailTab>('items');

  // Items tab state
  const [items, setItems] = useState<AdminLaunchItemDetail[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPagination, setItemsPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null);
  const [itemsStatusCounts, setItemsStatusCounts] = useState<Record<string, number>>({});
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsStatusFilter, setItemsStatusFilter] = useState('all');

  // Mints tab state
  const [mints, setMints] = useState<AdminLaunchMint[]>([]);
  const [mintsLoading, setMintsLoading] = useState(false);
  const [mintsPagination, setMintsPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null);
  const [mintsStatusCounts, setMintsStatusCounts] = useState<Record<string, number>>({});
  const [mintsPage, setMintsPage] = useState(1);
  const [mintsStatusFilter, setMintsStatusFilter] = useState('all');

  // Mint resolution state
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveAction, setResolveAction] = useState<'retry' | 'complete' | 'mark_failed' | null>(null);
  const [selectedMintForResolve, setSelectedMintForResolve] = useState<AdminLaunchMint | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveErrorMessage, setResolveErrorMessage] = useState('');
  const [resolveMintTxHash, setResolveMintTxHash] = useState('');
  const [resolveNftTokenId, setResolveNftTokenId] = useState('');
  const [resolveTransferTxHash, setResolveTransferTxHash] = useState('');
  const [resolveRequiresRefund, setResolveRequiresRefund] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [selectedMintIds, setSelectedMintIds] = useState<Set<string>>(new Set());

  // Settlements tab state
  const [settlements, setSettlements] = useState<AdminLaunchSettlement[]>([]);
  const [settlementsLoading, setSettlementsLoading] = useState(false);

  const loadLaunch = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminLaunchDetail(id);
      setLaunch(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load launch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLaunch();
  }, [id]);

  // Fetch mint contest data when launch loads
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setMintContestLoading(true);
    mintRewardsApi.getForLaunch(id).then((data) => {
      if (!cancelled) {
        setMintContest(data && 'contest' in data && data.contest ? data as ContestView : null);
      }
    }).catch(() => {
      // No contest or error - leave as null
    }).finally(() => {
      if (!cancelled) setMintContestLoading(false);
    });
    return () => { cancelled = true; };
  }, [id]);

  // Poll for IPFS progress updates (10s interval) when upload is in progress
  useEffect(() => {
    if (!launch || !id) return;
    // Only poll for standard launches that are approved but IPFS upload isn't complete
    if (launch.collection_type === 'composable') return;
    if (launch.ipfs_uploaded) return;
    if (!launch.admin_approved) return;
    if (!launch.ipfs_progress || launch.ipfs_progress.completed >= launch.ipfs_progress.total) return;

    const interval = setInterval(async () => {
      try {
        const data = await getAdminLaunchDetail(id);
        setLaunch(data);
      } catch {
        // Silently ignore polling errors
      }
    }, 10_000);

    return () => clearInterval(interval);
  }, [id, launch?.ipfs_progress?.completed, launch?.ipfs_progress?.total, launch?.ipfs_uploaded, launch?.admin_approved, launch?.collection_type]);

  const handleApprove = async () => {
    if (!id || !confirm('Are you sure you want to approve this launch?')) return;

    try {
      setActionLoading(true);
      await approveLaunch(id);
      notifications.success('Launch approved. The creator can now proceed with authorization.', {
        title: 'Approved',
      });
      await loadLaunch();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to approve launch', {
        title: 'Approval Failed',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSocialLinkUpdate = async (field: 'website_url' | 'twitter_handle' | 'discord_url', value: string | null) => {
    if (!id) return;
    try {
      const updated = await updateLaunchSocialLinks(id, { [field]: value });
      setLaunch((prev) => prev ? { ...prev, [field]: updated[field] } : prev);
      notifications.success('Updated successfully', { title: 'Saved' });
    } catch (err: any) {
      notifications.error(err.message || 'Failed to update', { title: 'Update Failed' });
      throw err;
    }
  };

  const handleAutoSettleToggle = async () => {
    if (!id || !launch) return;
    const enabling = !launch.auto_settle;
    const confirmMsg = enabling
      ? 'Enable auto-settle for this launch? Settlements will be approved automatically without manual review.'
      : 'Disable auto-settle? Settlements will require manual admin approval again.';
    if (!confirm(confirmMsg)) return;

    try {
      setAutoSettleLoading(true);
      const updated = await setAutoSettle(id, enabling);
      setLaunch((prev) => prev ? { ...prev, auto_settle: updated.auto_settle } : prev);
      notifications.success(
        enabling ? 'Auto-settle enabled. Settlements will be approved automatically.' : 'Auto-settle disabled. Settlements require manual approval.',
        { title: enabling ? 'Auto-Settle Enabled' : 'Auto-Settle Disabled' }
      );
    } catch (err: any) {
      notifications.error(err.message || 'Failed to update auto-settle setting', { title: 'Update Failed' });
    } finally {
      setAutoSettleLoading(false);
    }
  };

  // ==================== Claim Tokens ====================

  const loadClaimTokens = useCallback(async () => {
    if (!id) return;
    try {
      setClaimTokensLoading(true);
      setClaimTokens(await getLaunchClaimTokens(id));
    } catch (err: any) {
      notifications.error(err.message || 'Failed to load claim tokens', { title: 'Load Failed' });
    } finally {
      setClaimTokensLoading(false);
    }
  }, [id]);

  const handleGenerateClaimTokens = async () => {
    if (!id) return;
    if (
      !confirm(
        'Generate claim tokens for every item that does not already have one?\n\nThis is safe to run more than once — it tops up rather than duplicating.'
      )
    )
      return;

    try {
      setClaimTokensLoading(true);
      const result = await generateLaunchClaimTokens(id);

      if (result.created > 0) {
        // Tokens are stored hashed — this response is the only copy of the
        // plaintext links, so save it before anything can go wrong.
        downloadClaimLinksCsv(result.csv, launch?.slug || 'launch');
        setGeneratedLinks(result.links);
        notifications.success(
          `Generated ${result.created} claim link${result.created === 1 ? '' : 's'} and downloaded the CSV. These links cannot be shown again.`,
          { title: 'Save the CSV now' }
        );
      } else {
        notifications.success('Every piece already has a claim link — nothing to do.', {
          title: 'Claim Links',
        });
      }
      await loadClaimTokens();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to generate claim links', { title: 'Generate Failed' });
      setClaimTokensLoading(false);
    }
  };

  const handleVoidClaimToken = async (tokenId: string, serial: number | null) => {
    const reason = prompt(
      `Void the claim token for piece #${serial ?? '?'}?\n\nThis link stops working immediately. Generate again afterwards to issue a replacement link for the same piece.\n\nReason:`
    );
    if (reason === null) return;

    try {
      await voidLaunchClaimToken(tokenId, reason || 'Voided by admin');
      notifications.success('Claim token voided.', { title: 'Token Voided' });
      await loadClaimTokens();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to void claim token', { title: 'Void Failed' });
    }
  };

  const handleResetClaimToken = async (tokenId: string, serial: number | null) => {
    const reason = prompt(
      `Reset the stranded claim for piece #${serial ?? '?'}?\n\nThe mint for this link failed and no NFT exists on-chain. This voids the link and frees the piece so you can issue a replacement with Generate.\n\nThe collector's original link will stop working — send them the new one.\n\nReason:`
    );
    if (reason === null) return;

    try {
      await resetLaunchClaimToken(tokenId, reason || 'Reset after failed mint');
      notifications.success('Piece freed. Run Generate to issue a replacement link.', {
        title: 'Claim Reset',
      });
      await loadClaimTokens();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to reset claim', { title: 'Reset Failed' });
    }
  };

  const handleClaimOnlyToggle = async () => {
    if (!id || !launch) return;
    const enabling = !launch.claim_only;
    const confirmMsg = enabling
      ? 'Enable claim-only minting?\n\nThe public mint endpoint will be CLOSED for this launch. NFTs can then only be obtained by redeeming a claim link.'
      : 'Disable claim-only minting?\n\n⚠️ WARNING: this OPENS this launch to normal public minting. If the mint price is 0, anyone could mint the entire supply immediately.';
    if (!confirm(confirmMsg)) return;

    try {
      setClaimOnlyLoading(true);
      const updated = await setLaunchClaimOnly(id, enabling);
      setLaunch((prev) => (prev ? { ...prev, claim_only: updated.claim_only } : prev));
      notifications.success(
        enabling
          ? 'Claim-only enabled. Public minting is closed for this launch.'
          : 'Claim-only disabled. This launch is now open to public minting.',
        { title: enabling ? 'Claim-Only Enabled' : 'Claim-Only Disabled' }
      );
    } catch (err: any) {
      notifications.error(err.message || 'Failed to update claim-only setting', { title: 'Update Failed' });
    } finally {
      setClaimOnlyLoading(false);
    }
  };

  const handlePriceLockToggle = async () => {
    if (!id || !launch) return;
    const locking = !launch.public_phase_price_locked;
    const confirmMsg = locking
      ? 'Lock public phase price? The creator will not be able to change the price once the public phase starts.'
      : 'Unlock public phase price? The creator will be able to change the public mint price at any time, even during the public phase.';
    if (!confirm(confirmMsg)) return;

    try {
      setPriceLockLoading(true);
      const updated = await setPublicPhasePriceLocked(id, locking);
      setLaunch((prev) => prev ? { ...prev, public_phase_price_locked: updated.public_phase_price_locked } : prev);
      notifications.success(
        locking ? 'Public phase price locked. Price cannot be changed once public phase starts.' : 'Public phase price unlocked. Creator can change price at any time.',
        { title: locking ? 'Price Locked' : 'Price Unlocked' }
      );
    } catch (err: any) {
      notifications.error(err.message || 'Failed to update price lock setting', { title: 'Update Failed' });
    } finally {
      setPriceLockLoading(false);
    }
  };

  const handleServiceFeeSave = async () => {
    if (!id || !launch) return;
    const percentage = parseFloat(serviceFeeInput);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      notifications.error('Fee must be between 0 and 100', { title: 'Invalid Fee' });
      return;
    }
    try {
      setServiceFeeLoading(true);
      const updated = await setLaunchServiceFee(id, percentage);
      setLaunch((prev) => prev ? { ...prev, service_fee_percent: updated.service_fee_percent } : prev);
      setEditingServiceFee(false);
      notifications.success(`Service fee updated to ${percentage}%`, { title: 'Service Fee Updated' });
    } catch (err: any) {
      notifications.error(err.message || 'Failed to update service fee', { title: 'Update Failed' });
    } finally {
      setServiceFeeLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!id) return;

    try {
      setActionLoading(true);
      await rejectLaunch(id, reason);
      setShowRejectModal(false);
      notifications.success('Launch rejected. The creator can revise and resubmit.', {
        title: 'Rejected',
      });
      await loadLaunch();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to reject launch', {
        title: 'Rejection Failed',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!id || !confirm('This will reset error counters and resume minting. Continue?')) return;

    try {
      setResumeLoading(true);
      await resumeAutoPausedLaunch(id);
      notifications.success('Launch resumed. Error counters have been reset.', {
        title: 'Resumed',
      });
      await loadLaunch();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to resume launch', {
        title: 'Resume Failed',
      });
    } finally {
      setResumeLoading(false);
    }
  };

  const loadRefunds = async () => {
    if (!id) return;

    try {
      setRefundsLoading(true);
      const data = await getAdminLaunchRefunds(id);
      setRefunds(data.mints);
    } catch (err: any) {
      notifications.error(err.message || 'Failed to load refunds', {
        title: 'Error',
      });
    } finally {
      setRefundsLoading(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!id || !selectedRefund) return;

    // Validate tx hash format if provided
    if (refundTxHash && !/^[A-F0-9]{64}$/i.test(refundTxHash)) {
      notifications.error('Invalid transaction hash format. Must be 64 hex characters.', {
        title: 'Validation Error',
      });
      return;
    }

    try {
      setProcessingRefundId(selectedRefund.id);
      await processAdminRefund(id, selectedRefund.id, {
        notes: refundNotes || undefined,
        refund_tx_hash: refundTxHash || undefined,
      });
      notifications.success('Refund marked as processed', {
        title: 'Processed',
      });
      setShowRefundModal(false);
      setSelectedRefund(null);
      setRefundNotes('');
      setRefundTxHash('');
      await loadRefunds();
      await loadLaunch();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to process refund', {
        title: 'Error',
      });
    } finally {
      setProcessingRefundId(null);
    }
  };

  const openRefundModal = (refund: AdminRefundMint) => {
    setSelectedRefund(refund);
    setRefundNotes('');
    setRefundTxHash('');
    setShowRefundModal(true);
  };

  // Mint resolution helpers
  const isMintResolvable = (mint: AdminLaunchMint) =>
    mint.requires_admin_review || mint.status === 'minting' || mint.status === 'transferring';

  const openResolveModal = (mint: AdminLaunchMint, action: 'retry' | 'complete' | 'mark_failed') => {
    setSelectedMintForResolve(mint);
    setResolveAction(action);
    setResolveNotes('');
    setResolveErrorMessage('');
    setResolveMintTxHash('');
    setResolveNftTokenId('');
    setResolveTransferTxHash('');
    setResolveRequiresRefund(mint.payment_tx_hash !== null);
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    setShowResolveModal(false);
    setSelectedMintForResolve(null);
    setResolveAction(null);
  };

  const handleResolveMint = async () => {
    if (!id || !selectedMintForResolve || !resolveAction) return;

    // Validation
    if (resolveAction === 'complete') {
      if (selectedMintForResolve.status === 'minting') {
        if (!resolveMintTxHash || !resolveNftTokenId) {
          notifications.error('mint_tx_hash and nft_token_id are required', { title: 'Validation Error' });
          return;
        }
        if (!/^[A-F0-9]{64}$/i.test(resolveMintTxHash)) {
          notifications.error('Invalid transaction hash format. Must be 64 hex characters.', { title: 'Validation Error' });
          return;
        }
      } else {
        if (!resolveTransferTxHash) {
          notifications.error('transfer_tx_hash is required', { title: 'Validation Error' });
          return;
        }
        if (!/^[A-F0-9]{64}$/i.test(resolveTransferTxHash)) {
          notifications.error('Invalid transaction hash format. Must be 64 hex characters.', { title: 'Validation Error' });
          return;
        }
      }
    }

    if (resolveAction === 'mark_failed' && !resolveErrorMessage.trim()) {
      notifications.error('Error message is required', { title: 'Validation Error' });
      return;
    }

    try {
      setResolving(true);

      const data: ResolveMintRequest = { action: resolveAction };
      if (resolveNotes) data.notes = resolveNotes;

      if (resolveAction === 'complete') {
        if (selectedMintForResolve.status === 'minting') {
          data.mint_tx_hash = resolveMintTxHash;
          data.nft_token_id = resolveNftTokenId;
        } else {
          data.transfer_tx_hash = resolveTransferTxHash;
        }
      }

      if (resolveAction === 'mark_failed') {
        data.error_message = resolveErrorMessage;
        data.requires_refund = resolveRequiresRefund;
      }

      await resolveAdminMint(id, selectedMintForResolve.id, data);
      notifications.success(`Mint resolved: ${resolveAction}`, { title: 'Success' });
      closeResolveModal();
      await loadMints();
    } catch (err: any) {
      notifications.error(err.message || 'Failed to resolve mint', { title: 'Error' });
    } finally {
      setResolving(false);
    }
  };

  const handleBulkResolve = async (action: 'retry' | 'mark_failed') => {
    if (!id || selectedMintIds.size === 0) return;

    if (action === 'mark_failed') {
      // For bulk mark_failed, use a simple prompt-style approach
      const errorMsg = window.prompt('Enter error message for all selected mints:');
      if (!errorMsg) return;

      try {
        setResolving(true);
        const result = await bulkResolveAdminMints(id, {
          mint_ids: Array.from(selectedMintIds),
          action: 'mark_failed',
          error_message: errorMsg,
          requires_refund: true,
        });
        notifications.success(`${result.succeeded.length} resolved, ${result.failed.length} failed`, { title: 'Bulk Resolution' });
        setSelectedMintIds(new Set());
        await loadMints();
      } catch (err: any) {
        notifications.error(err.message || 'Failed to bulk resolve', { title: 'Error' });
      } finally {
        setResolving(false);
      }
    } else {
      if (!window.confirm(`Retry ${selectedMintIds.size} mint(s)? Make sure you have verified on Bithomp that no XRPL transactions were submitted for these mints.`)) return;

      try {
        setResolving(true);
        const result = await bulkResolveAdminMints(id, {
          mint_ids: Array.from(selectedMintIds),
          action: 'retry',
        });
        notifications.success(`${result.succeeded.length} retried, ${result.failed.length} failed`, { title: 'Bulk Resolution' });
        if (result.failed.length > 0) {
          notifications.error(result.failed.map((f) => `${f.mintId.slice(0, 8)}: ${f.error}`).join('\n'), { title: 'Some mints failed' });
        }
        setSelectedMintIds(new Set());
        await loadMints();
      } catch (err: any) {
        notifications.error(err.message || 'Failed to bulk resolve', { title: 'Error' });
      } finally {
        setResolving(false);
      }
    }
  };

  const toggleMintSelection = (mintId: string) => {
    setSelectedMintIds((prev) => {
      const next = new Set(prev);
      if (next.has(mintId)) next.delete(mintId);
      else next.add(mintId);
      return next;
    });
  };

  const toggleAllResolvableMints = () => {
    const resolvable = mints.filter(isMintResolvable);
    if (selectedMintIds.size === resolvable.length) {
      setSelectedMintIds(new Set());
    } else {
      setSelectedMintIds(new Set(resolvable.map((m) => m.id)));
    }
  };

  // Tab data loaders
  const loadItems = async (page = itemsPage, status = itemsStatusFilter) => {
    if (!id) return;
    try {
      setItemsLoading(true);
      const data = await getAdminLaunchItems(id, {
        page,
        limit: 50,
        status: status !== 'all' ? status : undefined,
      });
      setItems(data.items);
      setItemsPagination(data.pagination);
      setItemsStatusCounts(data.statusCounts);
    } catch (err: any) {
      notifications.error(err.message || 'Failed to load items', { title: 'Error' });
    } finally {
      setItemsLoading(false);
    }
  };

  const loadMints = async (page = mintsPage, status = mintsStatusFilter) => {
    if (!id) return;
    try {
      setMintsLoading(true);
      const data = await getAdminLaunchMints(id, {
        page,
        limit: 50,
        status: status !== 'all' ? status : undefined,
      });
      setMints(data.mints);
      setMintsPagination(data.pagination);
      setMintsStatusCounts(data.statusCounts);
    } catch (err: any) {
      notifications.error(err.message || 'Failed to load mints', { title: 'Error' });
    } finally {
      setMintsLoading(false);
    }
  };

  const loadSettlements = async () => {
    if (!id) return;
    try {
      setSettlementsLoading(true);
      const data = await getAdminLaunchSettlements(id);
      setSettlements(data.settlements);
    } catch (err: any) {
      notifications.error(err.message || 'Failed to load settlements', { title: 'Error' });
    } finally {
      setSettlementsLoading(false);
    }
  };

  // Set default tab for composable (no items tab)
  useEffect(() => {
    if (launch && launch.collection_type === 'composable' && activeTab === 'items') {
      setActiveTab('mints');
    }
  }, [launch]);

  // Load tab data when tab changes or launch loads
  useEffect(() => {
    if (!launch || loading) return;
    if (activeTab === 'items' && items.length === 0 && !itemsLoading) loadItems(1, 'all');
    if (activeTab === 'mints' && mints.length === 0 && !mintsLoading) loadMints(1, 'all');
    if (activeTab === 'settlements' && settlements.length === 0 && !settlementsLoading) loadSettlements();
  }, [activeTab, launch, loading]);

  const handleItemsPageChange = (page: number) => {
    setItemsPage(page);
    loadItems(page, itemsStatusFilter);
  };

  const handleItemsStatusChange = (status: string) => {
    setItemsStatusFilter(status);
    setItemsPage(1);
    loadItems(1, status);
  };

  const handleMintsPageChange = (page: number) => {
    setMintsPage(page);
    loadMints(page, mintsStatusFilter);
  };

  const handleMintsStatusChange = (status: string) => {
    setMintsStatusFilter(status);
    setMintsPage(1);
    loadMints(1, status);
  };

  const canApproveOrReject = launch?.status === 'pending_approval';
  const canResume = launch?.status === 'paused' && launch?.auto_paused_at;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin/launches"
            className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back to Launches
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {launch?.name || 'Launch Review'}
              </h1>
              {launch && (
                <div className="mt-2 flex items-center gap-3">
                  <LaunchStatusBadge status={launch.status} size="md" />
                  {launch.collection_type === 'composable' ? (
                    <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800">
                      Build Your Own
                    </span>
                  ) : launch.collection_type === 'edition' ? (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
                      Editions
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
                      Standard
                    </span>
                  )}
                  <span className="text-sm text-gray-500">/{launch.slug}</span>
                </div>
              )}
            </div>
            {canApproveOrReject && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Approve'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadLaunch} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading launch details...</p>
            </div>
          </div>
        )}

        {/* Launch Details */}
        {launch && !loading && (<>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Images */}
              <InfoCard title="Collection Images">
                <div className="space-y-4">
                  {launch.collection_type === 'composable' && launch.base_image_url && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Base Image
                      </label>
                      <img
                        src={launch.base_image_url}
                        alt="Base Image"
                        className="h-40 w-40 rounded-lg object-contain bg-gray-100 border border-gray-200"
                      />
                    </div>
                  )}
                  {launch.thumbnail_url && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Thumbnail
                      </label>
                      <img
                        src={launch.thumbnail_url}
                        alt="Thumbnail"
                        className="h-32 w-32 rounded-lg object-cover"
                      />
                    </div>
                  )}
                  {launch.banner_url && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Banner
                      </label>
                      <img
                        src={launch.banner_url}
                        alt="Banner"
                        className="w-full rounded-lg object-cover"
                        style={{
                          maxHeight: '200px',
                          objectPosition: `center ${(launch as any).banner_position_y ?? 50}%`,
                        }}
                      />
                    </div>
                  )}
                  {!launch.thumbnail_url && !launch.banner_url && !launch.base_image_url && (
                    <p className="text-sm text-gray-500">No images uploaded</p>
                  )}
                </div>
              </InfoCard>

              {/* Collection Details */}
              <InfoCard title="Collection Details">
                <InfoRow label="Name" value={launch.name} />
                <InfoRow label="Slug" value={`/${launch.slug}`} />
                <div className="py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap">
                    {launch.description || '-'}
                  </p>
                </div>
                <EditableInfoRow
                  label="Website"
                  value={launch.website_url}
                  onSave={(val) => handleSocialLinkUpdate('website_url', val)}
                  placeholder="https://example.com"
                  renderDisplay={(val) => (
                    <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm font-medium">
                      {val}
                    </a>
                  )}
                />
                <EditableInfoRow
                  label="Twitter"
                  value={launch.twitter_handle}
                  onSave={(val) => handleSocialLinkUpdate('twitter_handle', val)}
                  placeholder="username (without @)"
                  renderDisplay={(val) => (
                    <a
                      href={`https://twitter.com/${val.replace(/^@/, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm font-medium"
                    >
                      @{val.replace(/^@/, '')}
                    </a>
                  )}
                />
                <EditableInfoRow
                  label="Discord"
                  value={launch.discord_url}
                  onSave={(val) => handleSocialLinkUpdate('discord_url', val)}
                  placeholder="https://discord.gg/..."
                  renderDisplay={(val) => (
                    <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm font-medium">
                      Discord
                    </a>
                  )}
                />
              </InfoCard>

              {/* Creator Info */}
              <InfoCard title="Creator">
                <InfoRow
                  label="Username"
                  value={launch.creator_username || 'Not set'}
                />
                <InfoRow
                  label="Address"
                  value={
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(launch.creator_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-blue-600 hover:underline"
                    >
                      {launch.creator_address}
                    </a>
                  }
                />
              </InfoCard>

              {/* Rejection Reason (if any) */}
              {launch.admin_rejection_reason && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <h3 className="mb-2 font-semibold text-red-800">Previous Rejection Reason</h3>
                  <p className="text-sm text-red-700">{launch.admin_rejection_reason}</p>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Pricing & Supply */}
              <InfoCard title="Pricing & Supply">
                {launch.collection_type === 'composable' ? (
                  <>
                    <InfoRow
                      label="Total Trait Supply"
                      value={
                        launch.trait_categories
                          ? launch.trait_categories.reduce((sum, cat) =>
                              sum + cat.values.reduce((s, v) => s + v.total_supply, 0), 0
                            ).toLocaleString()
                          : '-'
                      }
                    />
                    <InfoRow label="Trait Categories" value={launch.trait_categories?.length ?? 0} />
                    <InfoRow label="Minted" value={launch.minted_count.toLocaleString()} />
                  </>
                ) : (
                  <>
                    <InfoRow label="Total Items" value={launch.total_items.toLocaleString()} />
                    <InfoRow label="Minted" value={launch.minted_count.toLocaleString()} />
                  </>
                )}
                {launch.allowlist_enabled && (
                  <InfoRow
                    label={launch.allowlist_2_enabled ? "Phase 1 Mint Price" : "Allowlist Mint Price"}
                    value={formatXrp(launch.allowlist_mint_price_drops)}
                  />
                )}
                {launch.allowlist_2_enabled && (
                  <InfoRow
                    label="Phase 2 Mint Price"
                    value={formatXrp(launch.allowlist_2_mint_price_drops)}
                  />
                )}
                <InfoRow
                  label="Public Mint Price"
                  value={formatXrp(launch.mint_price_drops)}
                />
                {launch.collection_type === 'composable' && (
                  <InfoRow
                    label="Build Your Own Fee"
                    value={launch.composition_fee_per_nft_drops > 0 ? formatXrp(launch.composition_fee_per_nft_drops) : 'Free'}
                  />
                )}
                <InfoRow
                  label="Max Per Wallet"
                  value={launch.limit_per_wallet?.toString() || 'Unlimited'}
                />
                <InfoRow label="Royalty" value={formatRoyalty(launch.transfer_fee)} />
              </InfoCard>

              {/* Allowlist */}
              <InfoCard title="Allowlist">
                <InfoRow
                  label="Phase 1 Enabled"
                  value={launch.allowlist_enabled ? 'Yes' : 'No'}
                />
                <InfoRow
                  label="Phase 2 Enabled"
                  value={launch.allowlist_2_enabled ? 'Yes' : 'No'}
                />
                <InfoRow
                  label="Allowlist Entries"
                  value={launch.allowlist_count.toLocaleString()}
                />
              </InfoCard>

              {/* Schedule */}
              <InfoCard title="Schedule">
                {launch.allowlist_enabled && (
                  <InfoRow
                    label={launch.allowlist_2_enabled ? "Phase 1 Start" : "Allowlist Start"}
                    value={
                      launch.allowlist_start_at
                        ? new Date(launch.allowlist_start_at).toLocaleString()
                        : 'Not set'
                    }
                  />
                )}
                {launch.allowlist_2_enabled && (
                  <InfoRow
                    label="Phase 2 Start"
                    value={
                      launch.allowlist_2_start_at
                        ? new Date(launch.allowlist_2_start_at).toLocaleString()
                        : 'Not set'
                    }
                  />
                )}
                <InfoRow
                  label="Public Start"
                  value={
                    launch.public_start_at
                      ? new Date(launch.public_start_at).toLocaleString()
                      : 'Not set'
                  }
                />
                <InfoRow
                  label="End Time"
                  value={
                    launch.end_at
                      ? new Date(launch.end_at).toLocaleString()
                      : 'No end time'
                  }
                />
              </InfoCard>

              {/* Fee Payment */}
              <InfoCard title="Platform Fee">
                <InfoRow
                  label="Fee Amount"
                  value={launch.fee_xrp ? `${launch.fee_xrp.toFixed(2)} XRP` : '-'}
                />
                <InfoRow
                  label="Fee Paid"
                  value={
                    launch.fee_paid ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-600">No</span>
                    )
                  }
                />
                {launch.fee_payment_tx_hash && (
                  <InfoRow
                    label="Payment TX"
                    value={
                      <a
                        href={`https://bithomp.com/explorer/${encodeURIComponent(launch.fee_payment_tx_hash)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-blue-600 hover:underline"
                      >
                        {launch.fee_payment_tx_hash.slice(0, 12)}...
                      </a>
                    }
                  />
                )}
              </InfoCard>

              {/* Admin Approval Status */}
              {launch.admin_approved && (
                <InfoCard title="Approval Status">
                  <InfoRow label="Approved" value="Yes" />
                  <InfoRow
                    label="Approved At"
                    value={
                      launch.admin_approved_at
                        ? new Date(launch.admin_approved_at).toLocaleString()
                        : '-'
                    }
                  />
                  <InfoRow
                    label="Approved By"
                    value={launch.admin_approved_by || '-'}
                  />
                </InfoCard>
              )}

              {/* Claim Tokens (physical NFC cards) */}
              <InfoCard title="Claim Links">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Claim-Only Minting</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {launch.claim_only
                        ? 'Public minting is CLOSED. NFTs can only be obtained by redeeming a claim token.'
                        : 'Public minting is OPEN. Anyone can mint from the collection page.'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        launch.claim_only ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {launch.claim_only ? 'Claim Only' : 'Public'}
                    </span>
                    <button
                      onClick={handleClaimOnlyToggle}
                      disabled={claimOnlyLoading}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                        launch.claim_only
                          ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {claimOnlyLoading ? 'Saving...' : launch.claim_only ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>

                {/* A free launch that is NOT claim-only can be drained by anyone the
                    moment it goes active — surface that loudly rather than relying
                    on whoever is reading the page to connect the two facts. */}
                {!launch.claim_only && Number(launch.mint_price_drops) === 0 && (
                  <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                    <strong>Warning:</strong> this launch is free (0 XRP) and open to public
                    minting. Once it goes active, anyone can mint the entire supply. Enable
                    claim-only if this drop should only be mintable via claim links.
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Claim Links</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        One unique link per copy. Links are stored hashed — Generate downloads
                        them once and they can never be shown again. Export gives status only.
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <button
                        onClick={loadClaimTokens}
                        disabled={claimTokensLoading}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {claimTokensLoading ? 'Loading...' : claimTokens ? 'Refresh' : 'Load'}
                      </button>
                      <button
                        onClick={handleGenerateClaimTokens}
                        disabled={claimTokensLoading}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  {generatedLinks && generatedLinks.length > 0 && (
                    <div className="mt-3 rounded-md border-2 border-amber-300 bg-amber-50 p-3">
                      <div className="text-xs font-semibold text-amber-900">
                        ⚠ Shown once — the CSV has downloaded. These links cannot be retrieved again.
                      </div>
                      <div className="mt-2 max-h-48 overflow-y-auto rounded bg-white p-2 font-mono text-[11px]">
                        {generatedLinks.map((l) => (
                          <div key={l.claim_url} className="truncate">
                            <span className="mr-2 font-semibold text-gray-600">#{l.serial ?? '?'}</span>
                            {l.claim_url}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setGeneratedLinks(null)}
                        className="mt-2 text-xs text-amber-900 underline"
                      >
                        I've saved them — hide
                      </button>
                    </div>
                  )}

                  {claimTokens && (
                    <>
                      <div className="mt-3 flex flex-wrap gap-3 text-xs">
                        <span className="rounded bg-gray-100 px-2 py-1 text-gray-700">
                          Total: <strong>{claimTokens.stats.total}</strong>
                        </span>
                        <span className="rounded bg-blue-100 px-2 py-1 text-blue-800">
                          Unclaimed: <strong>{claimTokens.stats.unclaimed}</strong>
                        </span>
                        <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                          Delivered: <strong>{claimTokens.stats.delivered}</strong>
                        </span>
                        {claimTokens.stats.stranded > 0 && (
                          <span className="rounded bg-orange-100 px-2 py-1 font-semibold text-orange-900">
                            ⚠ Stranded: <strong>{claimTokens.stats.stranded}</strong>
                          </span>
                        )}
                        {claimTokens.stats.void > 0 && (
                          <span className="rounded bg-red-100 px-2 py-1 text-red-800">
                            Void: <strong>{claimTokens.stats.void}</strong>
                          </span>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await exportClaimTokensCsv(launch.id);
                            } catch (err: any) {
                              notifications.error(err.message || 'Failed to export claim tokens', {
                                title: 'Export Failed',
                              });
                            }
                          }}
                          className="rounded bg-gray-800 px-2 py-1 text-white hover:bg-gray-900"
                        >
                          Export status
                        </button>
                      </div>

                      {claimTokens.tokens.length > 0 && (
                        <div className="mt-3 max-h-96 overflow-y-auto rounded border border-gray-200">
                          <table className="min-w-full text-xs">
                            <thead className="sticky top-0 bg-gray-50">
                              <tr>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-600">#</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-600">URL</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-600">Link</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-600">Mint</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-600">Claimed By</th>
                                <th className="px-2 py-1.5" />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {claimTokens.tokens.map((t) => (
                                <tr key={t.id} className={t.status === 'void' ? 'opacity-50' : ''}>
                                  <td className="px-2 py-1.5 font-medium text-gray-700">
                                    {t.edition_serial ?? '-'}
                                  </td>
                                  <td className="px-2 py-1.5 font-mono text-gray-500" title="Link prefix — the full link is not stored">
                                    {t.token_prefix}…
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <span
                                      className={`inline-flex rounded-full px-2 py-0.5 font-medium ${
                                        t.status === 'claimed'
                                          ? 'bg-green-100 text-green-800'
                                          : t.status === 'void'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {t.status}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    {t.needs_reset ? (
                                      <span
                                        className="inline-flex rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-900"
                                        title={t.mint_error || 'Mint failed'}
                                      >
                                        stranded
                                      </span>
                                    ) : t.nft_token_id ? (
                                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
                                        delivered
                                      </span>
                                    ) : t.mint_status ? (
                                      <span className="text-gray-600">{t.mint_status}</span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 font-mono text-gray-500">
                                    {t.claimed_by_address
                                      ? `${t.claimed_by_address.slice(0, 6)}…${t.claimed_by_address.slice(-4)}`
                                      : '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-right">
                                    {t.status === 'unclaimed' && (
                                      <button
                                        onClick={() => handleVoidClaimToken(t.id, t.edition_serial)}
                                        className="text-red-600 hover:underline"
                                      >
                                        Void
                                      </button>
                                    )}
                                    {t.needs_reset && (
                                      <button
                                        onClick={() => handleResetClaimToken(t.id, t.edition_serial)}
                                        className="font-medium text-orange-700 hover:underline"
                                      >
                                        Reset
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </InfoCard>

              {/* Settlement Settings */}
              <InfoCard title="Settlement Settings">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Auto-Settle</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {launch.auto_settle
                        ? 'Settlements are approved automatically without manual review'
                        : 'Settlements require manual admin approval before payment'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${launch.auto_settle ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {launch.auto_settle ? 'Enabled' : 'Disabled'}
                    </span>
                    <button
                      onClick={handleAutoSettleToggle}
                      disabled={autoSettleLoading}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                        launch.auto_settle
                          ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {autoSettleLoading ? 'Saving...' : launch.auto_settle ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>

                {/* Service Fee */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Mint Service Fee</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Charged to buyers on top of the mint price. Fee stays in platform wallet.
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0">
                    {editingServiceFee ? (
                      <>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={serviceFeeInput}
                          onChange={(e) => setServiceFeeInput(e.target.value)}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                        <button
                          onClick={handleServiceFeeSave}
                          disabled={serviceFeeLoading}
                          className="rounded-md px-3 py-1.5 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {serviceFeeLoading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingServiceFee(false)}
                          disabled={serviceFeeLoading}
                          className="rounded-md px-3 py-1.5 text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-semibold">{launch.service_fee_percent ?? 5}%</span>
                        <button
                          onClick={() => {
                            setServiceFeeInput(String(launch.service_fee_percent ?? 5));
                            setEditingServiceFee(true);
                          }}
                          className="rounded-md px-3 py-1.5 text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </InfoCard>

              {/* Referral Program (Admin Override) */}
              <InfoCard title="Referral Program">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Referrals</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {launch.referral_enabled
                        ? `Enabled at ${(launch.referral_bps || 0) / 100}% reward`
                        : 'Not enabled'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    {launch.referral_enabled && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={1}
                          max={25}
                          value={(launch.referral_bps || 500) / 100}
                          onChange={async (e) => {
                            const pct = Math.max(1, Math.min(25, Math.round(Number(e.target.value))));
                            try {
                              const updated = await setLaunchReferralConfig(launch.id, {
                                referral_enabled: true,
                                referral_bps: pct * 100,
                              });
                              setLaunch((prev) => prev ? { ...prev, referral_bps: pct * 100 } : prev);
                            } catch {}
                          }}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    )}
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${launch.referral_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {launch.referral_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <button
                      onClick={async () => {
                        const enabling = !launch.referral_enabled;
                        try {
                          await setLaunchReferralConfig(launch.id, {
                            referral_enabled: enabling,
                            referral_bps: enabling ? (launch.referral_bps || 500) : null,
                          });
                          setLaunch((prev) => prev ? {
                            ...prev,
                            referral_enabled: enabling,
                            referral_bps: enabling ? (prev.referral_bps || 500) : null,
                          } : prev);
                        } catch {}
                      }}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                        launch.referral_enabled
                          ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {launch.referral_enabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              </InfoCard>

              {/* Public Phase Price Lock */}
              <InfoCard title="Price Settings">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Public Phase Price Lock</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {launch.public_phase_price_locked !== false
                        ? 'Public mint price locks when the public phase starts (default behavior)'
                        : 'Creator can change the public mint price at any time, even during public phase'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${launch.public_phase_price_locked !== false ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {launch.public_phase_price_locked !== false ? 'Locked' : 'Unlocked'}
                    </span>
                    <button
                      onClick={handlePriceLockToggle}
                      disabled={priceLockLoading}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                        launch.public_phase_price_locked !== false
                          ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {priceLockLoading ? 'Saving...' : launch.public_phase_price_locked !== false ? 'Unlock' : 'Lock'}
                    </button>
                  </div>
                </div>
              </InfoCard>

              {/* IPFS Upload Progress (standard launches only, shown after admin approval) */}
              {launch.admin_approved && launch.collection_type !== 'composable' && launch.ipfs_progress && (
                <InfoCard title="IPFS Upload Progress">
                  {launch.ipfs_uploaded ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">All items uploaded to IPFS</span>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">
                            {launch.ipfs_progress.completed} of {launch.ipfs_progress.total} items uploaded
                          </span>
                          <span className="font-medium text-gray-900">
                            {launch.ipfs_progress.total > 0
                              ? Math.round((launch.ipfs_progress.completed / launch.ipfs_progress.total) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                            style={{
                              width: `${launch.ipfs_progress.total > 0
                                ? (launch.ipfs_progress.completed / launch.ipfs_progress.total) * 100
                                : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                      {launch.ipfs_progress.failed > 0 && (
                        <p className="text-sm text-red-600 font-medium">
                          {launch.ipfs_progress.failed} item{launch.ipfs_progress.failed !== 1 ? 's' : ''} failed (retry limit reached)
                        </p>
                      )}
                      {launch.ipfs_progress.completed < launch.ipfs_progress.total && (
                        <p className="text-xs text-gray-400 mt-2">Auto-refreshing every 10s</p>
                      )}
                    </>
                  )}
                </InfoCard>
              )}

              {/* Mint Contest */}
              <InfoCard title="Mint Contest">
                {mintContestLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : !mintContest ? (
                  <p className="text-sm text-gray-500">No mint contest configured for this launch.</p>
                ) : (
                  <div className="space-y-4">
                    <InfoRow label="Name" value={mintContest.contest.name} />
                    <InfoRow
                      label="Status"
                      value={
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          mintContest.contest.status === 'active' ? 'bg-green-100 text-green-800' :
                          mintContest.contest.status === 'ended' ? 'bg-yellow-100 text-yellow-800' :
                          mintContest.contest.status === 'resolved' ? 'bg-blue-100 text-blue-800' :
                          mintContest.contest.status === 'scheduled' ? 'bg-purple-100 text-purple-800' :
                          mintContest.contest.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {mintContest.contest.status}
                        </span>
                      }
                    />
                    {mintContest.contest.description && (
                      <InfoRow label="Description" value={mintContest.contest.description} />
                    )}
                    <InfoRow label="Starts" value={new Date(mintContest.contest.starts_at).toLocaleString()} />
                    <InfoRow label="Ends" value={new Date(mintContest.contest.ends_at).toLocaleString()} />
                    {mintContest.contest.drawn_by && (
                      <InfoRow label="Drawn By" value={mintContest.contest.drawn_by === 'auto' ? 'Automatic' : 'Manual'} />
                    )}
                    {mintContest.contest.resolved_at && (
                      <InfoRow label="Resolved At" value={new Date(mintContest.contest.resolved_at).toLocaleString()} />
                    )}

                    {/* Stats */}
                    <div className="border-t border-gray-200 pt-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Stats</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md bg-gray-50 p-3 text-center">
                          <div className="text-lg font-bold text-gray-900">{mintContest.stats.participants}</div>
                          <div className="text-xs text-gray-500">Participants</div>
                        </div>
                        <div className="rounded-md bg-gray-50 p-3 text-center">
                          <div className="text-lg font-bold text-gray-900">{mintContest.stats.entries}</div>
                          <div className="text-xs text-gray-500">Entries</div>
                        </div>
                      </div>
                    </div>

                    {/* Prize Tiers */}
                    {mintContest.prizeTiers.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Prize Tiers</div>
                        <div className="space-y-2">
                          {mintContest.prizeTiers.map((tier) => (
                            <div key={tier.id} className="rounded-md border border-gray-200 p-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">{tier.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                    tier.type === 'leaderboard' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {tier.type}
                                  </span>
                                  <span className="text-xs text-gray-500">{tier.winner_count} winner{tier.winner_count !== 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              {tier.description && (
                                <p className="mt-1 text-xs text-gray-500">{tier.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Winners */}
                    {mintContest.winners && mintContest.winners.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Winners</div>
                        <div className="space-y-1.5">
                          {mintContest.winners.map((winner) => (
                            <div key={winner.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                {winner.rank != null && (
                                  <span className="text-xs font-bold text-gray-400">#{winner.rank}</span>
                                )}
                                <a
                                  href={`https://bithomp.com/explorer/${encodeURIComponent(winner.wallet_address)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono text-xs text-blue-600 hover:underline"
                                >
                                  {winner.wallet_address.slice(0, 8)}...{winner.wallet_address.slice(-6)}
                                </a>
                              </div>
                              <span className="text-xs text-gray-500">{winner.tier_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Proof Links */}
                    {mintContest.contest.proof_links && mintContest.contest.proof_links.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Proof Links</div>
                        <div className="space-y-1">
                          {mintContest.contest.proof_links.map((link, i) => (
                            <a
                              key={i}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs text-blue-600 hover:underline truncate"
                            >
                              {link}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </InfoCard>

              {/* Error Tracking & Failures */}
              {(launch.failed_mints_count > 0 ||
                launch.mints_requiring_refund > 0 ||
                launch.auto_paused_at ||
                launch.consecutive_errors > 0) && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                  <h3 className="mb-4 text-lg font-semibold text-red-800">
                    Error Tracking & Failures
                  </h3>

                  {/* Auto-Pause Alert with Resume Button */}
                  {launch.auto_paused_at && (
                    <div className="mb-4 rounded-md bg-red-100 p-3 border border-red-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-2 text-red-600">⚠️</span>
                          <span className="font-medium text-red-800">Auto-Paused</span>
                        </div>
                        {canResume && (
                          <button
                            onClick={handleResume}
                            disabled={resumeLoading}
                            className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {resumeLoading ? 'Resuming...' : '▶ Resume Launch'}
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-red-700">
                        {launch.auto_pause_reason || 'Paused due to consecutive errors'}
                      </p>
                      <p className="mt-1 text-xs text-red-600">
                        Paused at: {new Date(launch.auto_paused_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <InfoRow
                      label="Consecutive Errors"
                      value={
                        <span className={launch.consecutive_errors > 0 ? 'text-red-600 font-medium' : ''}>
                          {launch.consecutive_errors}
                        </span>
                      }
                    />
                    {launch.last_error_at && (
                      <InfoRow
                        label="Last Error"
                        value={new Date(launch.last_error_at).toLocaleString()}
                      />
                    )}
                    <InfoRow
                      label="Failed Mints"
                      value={
                        <span className={launch.failed_mints_count > 0 ? 'text-red-600 font-medium' : ''}>
                          {launch.failed_mints_count}
                        </span>
                      }
                    />
                    <InfoRow
                      label="Mints Requiring Refund"
                      value={
                        <span className={launch.mints_requiring_refund > 0 ? 'text-red-600 font-bold' : ''}>
                          {launch.mints_requiring_refund}
                        </span>
                      }
                    />
                  </div>

                  {/* Refund Management Section */}
                  {launch.mints_requiring_refund > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-red-700 mb-3">
                        ⚠️ There are {launch.mints_requiring_refund} mint(s) that received payment but failed to mint.
                        These require manual refund processing.
                      </p>

                      <button
                        onClick={() => {
                          setShowRefundSection(!showRefundSection);
                          if (!showRefundSection && refunds.length === 0) {
                            loadRefunds();
                          }
                        }}
                        className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                      >
                        {showRefundSection ? 'Hide Refund Details' : 'View & Process Refunds'}
                      </button>

                      {showRefundSection && (
                        <div className="mt-4">
                          {refundsLoading ? (
                            <div className="text-sm text-gray-500">Loading refunds...</div>
                          ) : refunds.length === 0 ? (
                            <div className="text-sm text-gray-500">No refunds found</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-red-200 text-sm">
                                <thead>
                                  <tr className="text-left text-xs font-medium text-red-700 uppercase">
                                    <th className="py-2 pr-4">Buyer Address</th>
                                    <th className="py-2 pr-4">Amount</th>
                                    <th className="py-2 pr-4">Error</th>
                                    <th className="py-2 pr-4">Failed At</th>
                                    <th className="py-2">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-red-100">
                                  {refunds.map((refund) => (
                                    <tr key={refund.id}>
                                      <td className="py-2 pr-4 font-mono text-xs">
                                        <a
                                          href={`https://bithomp.com/explorer/${encodeURIComponent(refund.buyer_address)}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline"
                                        >
                                          {refund.buyer_address.slice(0, 8)}...{refund.buyer_address.slice(-6)}
                                        </a>
                                      </td>
                                      <td className="py-2 pr-4">
                                        {(refund.mint_price_drops / 1_000_000).toFixed(2)} XRP
                                      </td>
                                      <td className="py-2 pr-4 max-w-[200px] truncate" title={refund.error_message || ''}>
                                        {refund.error_message?.slice(0, 50) || 'Unknown error'}
                                        {(refund.error_message?.length || 0) > 50 && '...'}
                                      </td>
                                      <td className="py-2 pr-4 text-xs text-gray-600">
                                        {refund.failed_at ? new Date(refund.failed_at).toLocaleString() : '-'}
                                      </td>
                                      <td className="py-2">
                                        {refund.refund_processed_at ? (
                                          <span className="text-green-700 text-xs">
                                            Processed {new Date(refund.refund_processed_at).toLocaleDateString()}
                                          </span>
                                        ) : (
                                          <button
                                            onClick={() => openRefundModal(refund)}
                                            disabled={processingRefundId === refund.id}
                                            className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded disabled:opacity-50"
                                          >
                                            {processingRefundId === refund.id ? 'Processing...' : 'Mark Processed'}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Composable: Trait Categories - Full Width */}
            {launch.collection_type === 'composable' && launch.trait_categories && launch.trait_categories.length > 0 && (
              <div className="lg:col-span-2">
                <InfoCard title={`Trait Categories (${launch.trait_categories.length})`}>
                  <div className="space-y-6">
                    {launch.trait_categories.map((category) => (
                      <div key={category.id}>
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">{category.name}</h4>
                          <span className="text-xs text-gray-500">Layer {category.z_index}</span>
                          <span className="text-xs text-gray-400">
                            ({category.values.length} trait{category.values.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                          {category.values.map((value) => (
                            <div
                              key={value.id}
                              className="rounded-lg border border-gray-200 p-2"
                            >
                              {value.image_url ? (
                                <img
                                  src={value.image_url}
                                  alt={value.name}
                                  className="mb-2 aspect-square w-full rounded-md object-contain bg-gray-50"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = NO_IMAGE_PLACEHOLDER;
                                  }}
                                />
                              ) : (
                                <div className="mb-2 aspect-square w-full rounded-md bg-gray-100 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">No image</span>
                                </div>
                              )}
                              <div className="text-xs font-medium text-gray-900 truncate" title={value.name}>
                                {value.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Supply: {value.total_supply} / Minted: {value.minted_count}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </InfoCard>
              </div>
            )}

            {/* Editions: one card per artwork (not per expanded copy) */}
            {launch.collection_type === 'edition' && launch.editions && launch.editions.length > 0 && (
              <div className="lg:col-span-2">
                <InfoCard title={`Editions (${launch.editions.length} artwork${launch.editions.length !== 1 ? 's' : ''})`}>
                  <div className="grid gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {launch.editions.map((edition) => (
                      <div key={edition.id} className="rounded-lg border border-gray-200 p-3">
                        <img
                          src={edition.image_url || NO_IMAGE_PLACEHOLDER}
                          alt={edition.name}
                          className="mb-2 aspect-square w-full rounded-md object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = NO_IMAGE_PLACEHOLDER;
                          }}
                        />
                        <div className="text-sm font-medium text-gray-900 truncate" title={edition.name}>
                          {edition.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {edition.max_supply.toLocaleString()} {edition.max_supply === 1 ? 'copy' : 'copies'}
                          {' · '}
                          {edition.price_drops != null
                            ? `${(edition.price_drops / 1_000_000).toLocaleString()} XRP`
                            : 'launch price'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {edition.minted_count} minted · {edition.available_count} available
                        </div>
                      </div>
                    ))}
                  </div>
                </InfoCard>
              </div>
            )}

            {/* Build Your Own: Sample Previews */}
            {launch.collection_type === 'composable' && launch.base_image_url && launch.trait_categories && launch.trait_categories.length > 0 && (
              <div className="lg:col-span-2">
                <InfoCard title="Sample Previews">
                  <ComposePreviewGrid
                    baseImageUrl={launch.base_image_url}
                    categories={launch.trait_categories}
                  />
                </InfoCard>
              </div>
            )}

            {/* Standard: Sample Items - Full Width (editions use the Editions card above) */}
            {launch.collection_type !== 'composable' && launch.collection_type !== 'edition' && launch.sample_items.length > 0 && (
              <div className="lg:col-span-2">
                <InfoCard title={`Sample Items (${launch.sample_items.length})`}>
                  <div className="grid gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {launch.sample_items.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                        onClick={() => setSelectedSampleItem(item)}
                      >
                        {is3DModelUrl(item.original_image_key) ? (
                          item.thumbnail_key ? (
                            <img
                              src={item.thumbnail_key}
                              alt={item.name || `#${item.token_number}`}
                              className="mb-2 aspect-square w-full rounded-md object-cover"
                            />
                          ) : (
                            <div className="mb-2 aspect-square w-full rounded-md bg-gray-100 flex flex-col items-center justify-center gap-1">
                              <span style={{ fontSize: '32px' }}>🧊</span>
                              <span className="text-xs text-gray-500">3D Model</span>
                            </div>
                          )
                        ) : (
                          <img
                            src={item.original_image_key || NO_IMAGE_PLACEHOLDER}
                            alt={item.name || `#${item.token_number}`}
                            className="mb-2 aspect-square w-full rounded-md object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = NO_IMAGE_PLACEHOLDER;
                            }}
                          />
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {item.name || `#${item.token_number}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          #{item.token_number}
                        </div>
                      </div>
                    ))}
                  </div>
                </InfoCard>
              </div>
            )}
          </div>

          {/* ==================== Tabbed Detail Section ==================== */}
          <div className="mt-8">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex gap-4">
                {(launch.collection_type === 'composable'
                  ? (['mints', 'settlements'] as DetailTab[])
                  : (['items', 'mints', 'settlements'] as DetailTab[])
                ).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors capitalize ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Items Tab */}
            {activeTab === 'items' && (
              <div>
                <StatusFilterTabs
                  statusCounts={itemsStatusCounts}
                  activeStatus={itemsStatusFilter}
                  onStatusChange={handleItemsStatusChange}
                />
                {itemsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">No items found.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NFT Token ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved For</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Minted At</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transferred</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900 font-medium">{item.token_number}</td>
                            <td className="px-4 py-3 text-gray-900">{item.name || '-'}</td>
                            <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                            <td className="px-4 py-3">
                              {item.nft_token_id ? (
                                <TruncatedId value={item.nft_token_id} href={`https://bithomp.com/explorer/${encodeURIComponent(item.nft_token_id)}`} />
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              {item.reserved_for_address ? (
                                <TruncatedId value={item.reserved_for_address} href={`https://bithomp.com/explorer/${encodeURIComponent(item.reserved_for_address)}`} />
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {item.minted_at ? new Date(item.minted_at).toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {item.transferred_at ? new Date(item.transferred_at).toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 max-w-[200px]">
                              {item.error_message ? (
                                <span className="text-xs text-red-600 truncate block" title={item.error_message}>
                                  {item.error_message.slice(0, 50)}{item.error_message.length > 50 ? '...' : ''}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {itemsPagination && (
                      <PaginationControls
                        pagination={itemsPagination}
                        onPageChange={handleItemsPageChange}
                        label="items"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Mints Tab */}
            {activeTab === 'mints' && (
              <div>
                <StatusFilterTabs
                  statusCounts={mintsStatusCounts}
                  activeStatus={mintsStatusFilter}
                  onStatusChange={handleMintsStatusChange}
                />

                {/* Bulk action bar */}
                {selectedMintIds.size > 0 && (
                  <div className="flex items-center gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-2 mb-3">
                    <span className="text-sm font-medium text-blue-800">{selectedMintIds.size} selected</span>
                    <button
                      type="button"
                      onClick={() => handleBulkResolve('retry')}
                      disabled={resolving}
                      className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      Retry All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkResolve('mark_failed')}
                      disabled={resolving}
                      className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Mark All Failed
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedMintIds(new Set())}
                      className="text-xs text-gray-600 hover:text-gray-800 underline"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {mintsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                  </div>
                ) : mints.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">No mints found.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {mints.some(isMintResolvable) && (
                            <th className="px-3 py-3 w-8">
                              <input
                                type="checkbox"
                                checked={selectedMintIds.size > 0 && selectedMintIds.size === mints.filter(isMintResolvable).length}
                                onChange={toggleAllResolvableMints}
                                className="rounded border-gray-300"
                              />
                            </th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment TX</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NFT Token ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {mints.map((mint) => {
                          const resolvable = isMintResolvable(mint);
                          const canRetry = resolvable && (
                            (mint.status === 'minting' && !mint.mint_tx_hash) ||
                            (mint.status === 'transferring' && !mint.transfer_tx_hash)
                          );
                          return (
                          <tr key={mint.id} className={`hover:bg-gray-50 ${resolvable ? 'bg-red-50/30' : ''}`}>
                            {mints.some(isMintResolvable) && (
                              <td className="px-3 py-3">
                                {resolvable && (
                                  <input
                                    type="checkbox"
                                    checked={selectedMintIds.has(mint.id)}
                                    onChange={() => toggleMintSelection(mint.id)}
                                    className="rounded border-gray-300"
                                  />
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3">
                              <TruncatedId value={mint.buyer_address} href={`https://bithomp.com/explorer/${encodeURIComponent(mint.buyer_address)}`} />
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={mint.status} /></td>
                            <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                              {(mint.mint_price_drops / 1_000_000).toFixed(2)} XRP
                            </td>
                            <td className="px-4 py-3">
                              {mint.payment_tx_hash ? (
                                <TruncatedId value={mint.payment_tx_hash} href={`https://bithomp.com/explorer/${encodeURIComponent(mint.payment_tx_hash)}`} />
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              {mint.nft_token_id ? (
                                <TruncatedId value={mint.nft_token_id} href={`https://bithomp.com/explorer/${encodeURIComponent(mint.nft_token_id)}`} />
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                {mint.requires_mint_retry && (
                                  <span className="rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800" title="Requires mint retry">retry</span>
                                )}
                                {mint.requires_recovery && (
                                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-800" title="Requires recovery">recovery</span>
                                )}
                                {mint.requires_admin_review && (
                                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800" title="Requires admin review">review</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{mint.retry_count}</td>
                            <td className="px-4 py-3 max-w-[200px]">
                              {mint.error_message ? (
                                <span className="text-xs text-red-600 truncate block" title={mint.error_message}>
                                  {mint.error_message.slice(0, 50)}{mint.error_message.length > 50 ? '...' : ''}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                              {new Date(mint.created_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-3">
                              {resolvable && (
                                <div className="flex gap-1">
                                  {canRetry && (
                                    <button
                                      type="button"
                                      onClick={() => openResolveModal(mint, 'retry')}
                                      className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                                    >
                                      Retry
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openResolveModal(mint, 'complete')}
                                    className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200"
                                  >
                                    Complete
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openResolveModal(mint, 'mark_failed')}
                                    className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200"
                                  >
                                    Fail
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {mintsPagination && (
                      <PaginationControls
                        pagination={mintsPagination}
                        onPageChange={handleMintsPageChange}
                        label="mints"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Settlements Tab */}
            {activeTab === 'settlements' && (
              <div>
                {settlementsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                  </div>
                ) : settlements.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">No settlements found.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (XRP)</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mints</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TX Hash</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved By</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {settlements.map((s) => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                            <td className="px-4 py-3 text-gray-900 font-medium">
                              {(s.amount_drops / 1_000_000).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-gray-900">{s.mint_count}</td>
                            <td className="px-4 py-3">
                              {s.tx_hash ? (
                                <TruncatedId value={s.tx_hash} href={`https://bithomp.com/explorer/${encodeURIComponent(s.tx_hash)}`} />
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {s.approved_by || '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                              {new Date(s.created_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                              {s.completed_at ? new Date(s.completed_at).toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 max-w-[200px]">
                              {s.error_message ? (
                                <span className="text-xs text-red-600 truncate block" title={s.error_message}>
                                  {s.error_message.slice(0, 50)}{s.error_message.length > 50 ? '...' : ''}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>)}

        {/* Reject Modal */}
        <RejectModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          onConfirm={handleReject}
          isLoading={actionLoading}
        />

        {/* Process Refund Modal */}
        {showRefundModal && selectedRefund && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Mark Refund as Processed</h3>
              <div className="mb-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Buyer:</span>
                  <span className="font-mono">{selectedRefund.buyer_address.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount:</span>
                  <span>{(selectedRefund.mint_price_drops / 1_000_000).toFixed(2)} XRP</span>
                </div>
                {selectedRefund.payment_tx_hash && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Payment TX:</span>
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(selectedRefund.payment_tx_hash)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono text-xs"
                    >
                      {selectedRefund.payment_tx_hash.slice(0, 12)}...
                    </a>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Transaction Hash (recommended)
                </label>
                <input
                  type="text"
                  value={refundTxHash}
                  onChange={(e) => setRefundTxHash(e.target.value.toUpperCase())}
                  placeholder="64-character hex transaction hash"
                  maxLength={64}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the XRPL transaction hash of the refund payment for audit trail.
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e.target.value)}
                  placeholder="Any additional notes about this refund..."
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRefundModal(false);
                    setSelectedRefund(null);
                    setRefundNotes('');
                    setRefundTxHash('');
                  }}
                  disabled={processingRefundId !== null}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleProcessRefund}
                  disabled={processingRefundId !== null}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {processingRefundId ? 'Processing...' : 'Mark as Processed'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Mint Resolution Modal */}
        {showResolveModal && selectedMintForResolve && resolveAction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                {resolveAction === 'retry' && 'Retry Stuck Mint'}
                {resolveAction === 'complete' && 'Complete Mint with On-Chain Data'}
                {resolveAction === 'mark_failed' && 'Mark Mint as Failed'}
              </h3>

              {/* Mint details */}
              <div className="mb-4 space-y-2 text-sm rounded-md bg-gray-50 p-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Mint ID:</span>
                  <span className="font-mono text-xs">{selectedMintForResolve.id.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span className="font-medium">{selectedMintForResolve.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Buyer:</span>
                  <a
                    href={`https://bithomp.com/explorer/${encodeURIComponent(selectedMintForResolve.buyer_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono text-xs"
                  >
                    {selectedMintForResolve.buyer_address.slice(0, 12)}...
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Price:</span>
                  <span>{(selectedMintForResolve.mint_price_drops / 1_000_000).toFixed(2)} XRP</span>
                </div>
                {selectedMintForResolve.payment_tx_hash && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Payment TX:</span>
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(selectedMintForResolve.payment_tx_hash)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono text-xs"
                    >
                      {selectedMintForResolve.payment_tx_hash.slice(0, 12)}...
                    </a>
                  </div>
                )}
                {selectedMintForResolve.mint_tx_hash && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Mint TX:</span>
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(selectedMintForResolve.mint_tx_hash)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono text-xs"
                    >
                      {selectedMintForResolve.mint_tx_hash.slice(0, 12)}...
                    </a>
                  </div>
                )}
                {selectedMintForResolve.nft_token_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">NFT Token:</span>
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(selectedMintForResolve.nft_token_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono text-xs"
                    >
                      {selectedMintForResolve.nft_token_id.slice(0, 12)}...
                    </a>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Stuck since:</span>
                  <span className="text-xs">{new Date(selectedMintForResolve.created_at).toLocaleString()}</span>
                </div>
              </div>

              {/* Retry warning */}
              {resolveAction === 'retry' && (
                <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3">
                  <p className="text-sm font-medium text-amber-800">Double-mint risk</p>
                  <p className="mt-1 text-xs text-amber-700">
                    Before retrying, verify on{' '}
                    <a
                      href={`https://bithomp.com/explorer/${encodeURIComponent(selectedMintForResolve.buyer_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium"
                    >
                      Bithomp
                    </a>
                    {' '}that no NFTokenMint transaction was submitted from the minter wallet around {new Date(selectedMintForResolve.created_at).toLocaleString()}.
                    If a transaction was submitted, use "Complete" with the on-chain data instead.
                  </p>
                </div>
              )}

              {/* Complete fields */}
              {resolveAction === 'complete' && selectedMintForResolve.status === 'minting' && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mint TX Hash *</label>
                    <input
                      type="text"
                      value={resolveMintTxHash}
                      onChange={(e) => setResolveMintTxHash(e.target.value.toUpperCase())}
                      placeholder="64-character hex transaction hash"
                      maxLength={64}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NFT Token ID *</label>
                    <input
                      type="text"
                      value={resolveNftTokenId}
                      onChange={(e) => setResolveNftTokenId(e.target.value.toUpperCase())}
                      placeholder="NFT token ID from the mint transaction"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {resolveAction === 'complete' && selectedMintForResolve.status === 'transferring' && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer TX Hash *</label>
                    <input
                      type="text"
                      value={resolveTransferTxHash}
                      onChange={(e) => setResolveTransferTxHash(e.target.value.toUpperCase())}
                      placeholder="64-character hex transaction hash"
                      maxLength={64}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Mark failed fields */}
              {resolveAction === 'mark_failed' && (
                <div className="mb-4 space-y-3">
                  <div className="rounded-md bg-red-50 border border-red-200 p-3">
                    <p className="text-xs text-red-700">
                      This will mark the mint as permanently failed and free the reserved item for other buyers.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Error Message *</label>
                    <input
                      type="text"
                      value={resolveErrorMessage}
                      onChange={(e) => setResolveErrorMessage(e.target.value)}
                      placeholder="Reason for failure"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={resolveRequiresRefund}
                      onChange={(e) => setResolveRequiresRefund(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Requires refund</span>
                  </label>
                </div>
              )}

              {/* Notes (all actions) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Any additional context..."
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeResolveModal}
                  disabled={resolving}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResolveMint}
                  disabled={resolving}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                    resolveAction === 'mark_failed'
                      ? 'bg-red-600 hover:bg-red-700'
                      : resolveAction === 'retry'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {resolving ? 'Processing...' : (
                    resolveAction === 'retry' ? 'Confirm Retry' :
                    resolveAction === 'complete' ? 'Mark Complete' :
                    'Mark Failed'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Item Metadata Modal */}
        {selectedSampleItem && (
          <ItemMetadataModal
            item={selectedSampleItem}
            onClose={() => setSelectedSampleItem(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
